// ✅ src/pages/VehicleV2/BatteryCardReport_Print.jsx
// FULL WORKING PRINT PAGE (single row)
// - gets batteryId from navigate state OR URL query (?batteryId=3046)
// - calls: GET http://nissi-india.com/api/batteryCardReport/print/:batteryId
// - maps your DB fields (fld_*) correctly
// - prints ONE row in the table

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/batteryCardReport";

/* ---------------- helpers ---------------- */
const pad2 = (n) => String(n).padStart(2, "0");

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const fmtMoney = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const calcWarrantyDays = (fromIso, toIso) => {
  if (!fromIso || !toIso) return "";
  const a = new Date(fromIso);
  const b = new Date(toIso);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return "";
  const days = Math.floor((b - a) / (1000 * 60 * 60 * 24));
  return Number.isFinite(days) ? `${days} Days` : "";
};

// ✅ exact mapping from your response row (tbl_vehicle_batterycard_mas)
function normalizeRowFromDb(r) {
  const expectedLife = `${r?.fld_expectedlifeyear ?? ""} ${r?.fld_expectedlifemonth ?? ""}`.trim();
  const warrantyPeriod = calcWarrantyDays(r?.fld_warrentyfromdate, r?.fld_warrentytodate);

  // NOTE:
  // your row does NOT contain vehicle code/name/regno or branch/supplier names,
  // so we show IDs for those columns unless you join on backend.
  return {
    sno: 1,

    // shown in your table as "Date" + "Issued Date"
    date: fmtDate(r?.fld_purchaseddate || r?.fld_dateofreceipt),
    issuedDate: fmtDate(r?.fld_purchaseddate || r?.fld_dateofreceipt),

    vehicleMainType: "", // needs backend join
    vehicleCode: r?.fld_vehicleid ? String(r.fld_vehicleid) : "",
    vehicleName: "", // needs backend join
    vehicleRange: "", // needs backend join (veh regno)

    batteryCode: r?.fld_batterycode ?? "",
    batteryPartsNo: r?.fld_battery_serialno ?? "", // column name in print table
    batteryType: r?.fld_batterytype ?? "",
    batteryMake: r?.fld_batterymake ?? "",

    naturalBranch: r?.fld_branchid ? String(r.fld_branchid) : "",
    currentBranch: r?.fld_startingbranchid ? String(r.fld_startingbranchid) : "",

    purchasedAmt: fmtMoney(r?.fld_purchaseamt),
    expectedUse: expectedLife,
    warrantyPeriod,

    supplier: r?.fld_purchasedfrom ? String(r.fld_purchasedfrom) : "",
    purposeOfPurchase: r?.fld_remarks ?? "",
  };
}

export default function BatteryCardReportPrint() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // ✅ batteryId from state OR query string
  const batteryIdFromState = location?.state?.batteryId;
  const batteryIdFromQuery = searchParams.get("batteryId");
  const batteryId = useMemo(
    () => Number(batteryIdFromState || batteryIdFromQuery || 0),
    [batteryIdFromState, batteryIdFromQuery]
  );

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [dbRow, setDbRow] = useState(null);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        if (!batteryId) {
          setErr("Battery ID missing");
          setLoading(false);
          return;
        }

        setLoading(true);
        setErr("");

        const res = await axios.get(`${API_BASE}/print/${batteryId}`);
        const row = res?.data?.row; // ✅ your API returns { row: { fld_*... } }

        if (!row) throw new Error("Row not found in response");

        if (alive) setDbRow(row);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.message || e.message || "Failed to load print data");
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [batteryId]);

  const printRow = useMemo(() => (dbRow ? normalizeRowFromDb(dbRow) : null), [dbRow]);

  return (
    <div className="w-full bg-white print:max-w-none print:mx-0">
      {/* Print Styles */}
      <style>{`
        @media print {
          body { margin: 0; padding: 10px; font-size: 8pt; }
          .no-print { display: none !important; }
          table { border-collapse: collapse; font-size: 7pt; }
          th, td { border: 1px solid #000 !important; padding: 1px !important; }
          .print-landscape { 
            size: A4 landscape; 
            margin: 0.3in;
          }
        }
        @page {
          size: A4 landscape;
          margin: 0.3in;
        }
      `}</style>

      <div className="p-2 print:p-1 print-landscape">
        {/* Top actions */}
        <div className="no-print flex items-center justify-between mb-3">
          <button onClick={() => navigate(-1)} className="px-4 py-2 border rounded hover:bg-gray-50">
            ← Back
          </button>
          <button
            onClick={() => window.print()}
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow"
          >
            🖨️ Print
          </button>
        </div>

        {/* Loading/Error */}
        {loading ? (
          <div className="no-print p-4 border rounded bg-gray-50 text-sm">Loading print data...</div>
        ) : err ? (
          <div className="no-print p-4 border rounded bg-red-50 text-sm text-red-700">{err}</div>
        ) : null}

        {/* Header */}
        <div className="border-2 border-black mb-2">
          <div className="flex items-center p-2">
            <div className="w-20 h-16 border border-black flex items-center justify-center mr-4 bg-gray-800 text-white font-bold">
              <div className="text-center">
                <div className="text-xs">NISSI</div>
                <div className="text-xs">INFOTECH</div>
                <div className="text-xs">PVT LTD</div>
              </div>
            </div>

            <div className="flex-1 text-center">
              <h1 className="text-lg font-bold">Nissi Infotech</h1>
              <div className="text-sm font-semibold">CORE CONSTRUCTION</div>
              <div className="text-xs">Battery Card Print</div>
              <div className="text-xs">Battery ID: {batteryId || "-"}</div>
            </div>
          </div>
        </div>

        {/* Report Title */}
        <div className="text-center mb-2 border-2 border-black py-1">
          <h2 className="text-base font-bold">Battery Card Master Award</h2>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border-2 border-black text-xs">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "30px" }}>
                  S No
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "80px" }}>
                  Date
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "80px" }}>
                  Vehicle Main Type
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "70px" }}>
                  Vehicle Code
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "80px" }}>
                  Vehicle Name
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "80px" }}>
                  Vehicle Range
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "100px" }}>
                  Battery Code
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "80px" }}>
                  Battery Parts No
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "70px" }}>
                  Battery Type
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "70px" }}>
                  Battery Mass
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "70px" }}>
                  Natural Branch
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "70px" }}>
                  Current Branch
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "70px" }}>
                  Purchased Amt
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "70px" }}>
                  Issued Date
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "70px" }}>
                  Expected Use
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "70px" }}>
                  Warranty Period
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "50px" }}>
                  Supplier
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "120px" }}>
                  Purpose of Purchase
                </th>
              </tr>
            </thead>

            <tbody>
              {!printRow ? (
                <tr>
                  <td colSpan={18} className="border border-black px-2 py-2 text-center">
                    No data to print.
                  </td>
                </tr>
              ) : (
                <tr className="bg-white">
                  <td className="border border-black px-1 py-1 text-center">{printRow.sno}</td>
                  <td className="border border-black px-1 py-1 text-center">{printRow.date}</td>
                  <td className="border border-black px-1 py-1">{printRow.vehicleMainType}</td>
                  <td className="border border-black px-1 py-1 text-center">{printRow.vehicleCode}</td>
                  <td className="border border-black px-1 py-1">{printRow.vehicleName}</td>
                  <td className="border border-black px-1 py-1">{printRow.vehicleRange}</td>
                  <td className="border border-black px-1 py-1">{printRow.batteryCode}</td>
                  <td className="border border-black px-1 py-1">{printRow.batteryPartsNo}</td>
                  <td className="border border-black px-1 py-1">{printRow.batteryType}</td>
                  <td className="border border-black px-1 py-1">{printRow.batteryMake}</td>
                  <td className="border border-black px-1 py-1">{printRow.naturalBranch}</td>
                  <td className="border border-black px-1 py-1">{printRow.currentBranch}</td>
                  <td className="border border-black px-1 py-1 text-right">{printRow.purchasedAmt}</td>
                  <td className="border border-black px-1 py-1 text-center">{printRow.issuedDate}</td>
                  <td className="border border-black px-1 py-1 text-center">{printRow.expectedUse}</td>
                  <td className="border border-black px-1 py-1 text-center">{printRow.warrantyPeriod}</td>
                  <td className="border border-black px-1 py-1 text-center">{printRow.supplier}</td>
                  <td className="border border-black px-1 py-1">{printRow.purposeOfPurchase}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="mt-2 text-xs border-2 border-black p-1">
          <p>Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com</p>
        </div>

        {/* Print Button */}
        <div className="mt-4 text-center no-print">
          <button
            onClick={() => window.print()}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow-lg transition-colors duration-200"
          >
            🖨️ Print Report
          </button>
        </div>
      </div>
    </div>
  );
}
